/**
 * Cookie Consent Component
 * GDPR-compliant cookie consent banner with category management
 */

// Cookie categories
export type CookieCategory = 'necessary' | 'analytics' | 'functional' | 'marketing';

export interface ConsentPreferences {
  necessary: boolean; // Always true
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  timestamp: number;
}

interface CookieConsentConfig {
  matomoEnabled: boolean;
  metaPixelId: string;
  googleAdsId: string;
}

declare global {
  interface Window {
    _paq?: unknown[][];
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    cookieConsentConfig?: CookieConsentConfig;
  }
}

const STORAGE_KEY = 'cookie_consent';
const CONSENT_EXPIRY_DAYS = 365; // 12 months - GDPR recommends renewal

class CookieConsent {
  private banner: HTMLElement | null = null;
  private settingsPanel: HTMLElement | null = null;
  private fab: HTMLElement | null = null;
  private preferences: ConsentPreferences;
  private config: CookieConsentConfig;

  constructor() {
    this.config = window.cookieConsentConfig || {
      matomoEnabled: true,
      metaPixelId: '',
      googleAdsId: '',
    };

    this.preferences = this.loadPreferences();
    this.init();
  }

  private init(): void {
    // Get references
    this.fab = document.getElementById('cookie-settings-fab');
    this.banner = document.getElementById('cookie-consent-banner');
    this.settingsPanel = document.getElementById('cookie-consent-settings');

    // Check if we need to show the banner
    if (!this.hasValidConsent()) {
      this.showBanner();
      this.hideFab();
    } else {
      // Apply existing preferences and show FAB
      this.applyPreferences();
      this.showFab();
    }

    // Global event delegation for all cookie consent buttons
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;

      // Open settings modal
      if (target.closest('[data-cookie-settings]')) {
        e.preventDefault();
        this.showSettings();
        return;
      }

      // Accept all (banner or modal)
      if (target.closest('[data-accept-all]')) {
        e.preventDefault();
        this.acceptAll();
        return;
      }

      // Accept necessary only
      if (target.closest('[data-accept-necessary]')) {
        e.preventDefault();
        this.acceptNecessary();
        return;
      }

      // Show settings from banner
      if (target.closest('[data-show-settings]')) {
        e.preventDefault();
        this.showSettings();
        return;
      }

      // Save settings
      if (target.closest('[data-save-settings]')) {
        e.preventDefault();
        this.saveSettings();
        return;
      }

      // Close settings modal
      if (target.closest('[data-close-settings]')) {
        e.preventDefault();
        this.hideSettings();
        return;
      }

      // Click on modal backdrop
      if (target.id === 'cookie-consent-settings') {
        this.hideSettings();
        return;
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'Escape' &&
        this.settingsPanel &&
        !this.settingsPanel.classList.contains('hidden')
      ) {
        this.hideSettings();
      }
    });
  }

  private loadPreferences(): ConsentPreferences {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return JSON.parse(stored) as ConsentPreferences;
      } catch {
        // Invalid JSON, return defaults
      }
    }
    return this.getDefaultPreferences();
  }

  private getDefaultPreferences(): ConsentPreferences {
    return {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
      timestamp: 0,
    };
  }

  private hasValidConsent(): boolean {
    if (this.preferences.timestamp === 0) {
      return false;
    }

    // Check if consent has expired (GDPR recommends renewal)
    const expiryMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const isExpired = Date.now() - this.preferences.timestamp > expiryMs;

    return !isExpired;
  }

  private savePreferences(): void {
    this.preferences.timestamp = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.preferences));
  }

  private showBanner(): void {
    if (!this.banner) return;
    this.banner.classList.remove('hidden');
    this.banner.setAttribute('aria-hidden', 'false');
  }

  private hideBanner(): void {
    if (this.banner) {
      this.banner.classList.add('hidden');
      this.banner.setAttribute('aria-hidden', 'true');
    }
  }

  private showFab(): void {
    if (this.fab) {
      this.fab.classList.remove('hidden');
    }
  }

  private hideFab(): void {
    if (this.fab) {
      this.fab.classList.add('hidden');
    }
  }

  private showSettings(): void {
    this.settingsPanel = document.getElementById('cookie-consent-settings');
    if (!this.settingsPanel) return;

    // Hide FAB while modal is open
    this.hideFab();

    // Update checkboxes with current preferences
    this.updateSettingsUI();

    this.settingsPanel.classList.remove('hidden');
    this.settingsPanel.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  private hideSettings(): void {
    if (this.settingsPanel) {
      this.settingsPanel.classList.add('hidden');
      this.settingsPanel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    // Show FAB again if user already has consent
    if (this.hasValidConsent()) {
      this.showFab();
    }
  }

  private updateSettingsUI(): void {
    if (!this.settingsPanel) return;

    const categories: CookieCategory[] = ['analytics', 'functional', 'marketing'];
    categories.forEach((category) => {
      const checkbox = this.settingsPanel?.querySelector(
        `[data-category="${category}"]`
      ) as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = this.preferences[category];
      }
    });
  }

  private acceptAll(): void {
    this.preferences = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
      timestamp: Date.now(),
    };
    this.savePreferences();
    this.applyPreferences();
    this.hideBanner();
    this.hideSettings();
    this.showFab();
  }

  private acceptNecessary(): void {
    this.preferences = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
      timestamp: Date.now(),
    };
    this.savePreferences();
    this.applyPreferences();
    this.hideBanner();
    this.hideSettings();
    this.showFab();
  }

  private saveSettings(): void {
    if (!this.settingsPanel) return;

    const categories: CookieCategory[] = ['analytics', 'functional', 'marketing'];
    categories.forEach((category) => {
      const checkbox = this.settingsPanel?.querySelector(
        `[data-category="${category}"]`
      ) as HTMLInputElement;
      if (checkbox) {
        this.preferences[category] = checkbox.checked;
      }
    });

    this.savePreferences();
    this.applyPreferences();
    this.hideBanner();
    this.hideSettings();
    this.showFab();
  }

  private applyPreferences(): void {
    // Apply Matomo consent
    this.applyMatomoConsent();

    // Apply Meta Pixel consent
    this.applyMetaPixelConsent();

    // Apply Google Ads consent
    this.applyGoogleAdsConsent();

    // Apply YouTube embeds
    this.applyYouTubeConsent();

    // Dispatch event for other scripts
    window.dispatchEvent(
      new CustomEvent('cookieConsentUpdated', {
        detail: this.preferences,
      })
    );
  }

  private applyMatomoConsent(): void {
    if (!this.config.matomoEnabled) return;

    window._paq = window._paq || [];

    if (this.preferences.analytics) {
      // User consented to analytics - load Matomo and give consent
      this.loadMatomoTracker();
      window._paq.push(['setCookieConsentGiven']);
      window._paq.push(['rememberCookieConsentGiven']);
    }
  }

  private loadMatomoTracker(): void {
    // Check if already loaded
    if (document.querySelector('script[src*="matomo.wetell.de/piwik.js"]')) {
      return;
    }

    const u = '//matomo.wetell.de/';
    window._paq = window._paq || [];
    window._paq.push(['setTrackerUrl', u + 'piwik.php']);
    window._paq.push(['setSiteId', '8']);
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);

    const script = document.createElement('script');
    script.async = true;
    script.src = u + 'piwik.js';
    document.head.appendChild(script);
  }

  private applyMetaPixelConsent(): void {
    if (!this.config.metaPixelId) return;

    if (this.preferences.marketing) {
      // Initialize Meta Pixel if consented
      this.initMetaPixel(this.config.metaPixelId);
    }
  }

  private initMetaPixel(pixelId: string): void {
    if (window.fbq) return; // Already initialized

    // Load Meta Pixel script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    // Initialize fbq queue using standard pattern
    interface FbqFunction {
      (...args: unknown[]): void;
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[];
      version: string;
      loaded: boolean;
    }

    const fbq: FbqFunction = Object.assign(
      function (...args: unknown[]) {
        if (fbq.callMethod) {
          fbq.callMethod(...args);
        } else {
          fbq.queue.push(args);
        }
      },
      {
        queue: [] as unknown[],
        version: '2.0',
        loaded: true,
      }
    );

    window.fbq = fbq;
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
  }

  private applyGoogleAdsConsent(): void {
    if (!this.config.googleAdsId) return;

    if (this.preferences.marketing) {
      this.initGoogleAds(this.config.googleAdsId);
    }
  }

  private initGoogleAds(adsId: string): void {
    if (window.gtag) return; // Already initialized

    // Load gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${adsId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function (...args: unknown[]) {
      window.dataLayer?.push(args);
    };
    window.gtag('js', new Date());
    window.gtag('config', adsId);
  }

  private applyYouTubeConsent(): void {
    const youtubePlaceholders = document.querySelectorAll('[data-youtube-placeholder]');

    if (this.preferences.functional) {
      // Replace placeholders with actual embeds
      youtubePlaceholders.forEach((placeholder) => {
        const videoId = placeholder.getAttribute('data-youtube-id');
        if (videoId) {
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}`;
          iframe.width = '100%';
          iframe.height = '100%';
          iframe.setAttribute('frameborder', '0');
          iframe.allow =
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.classList.add('absolute', 'inset-0', 'w-full', 'h-full');
          placeholder.replaceWith(iframe);
        }
      });
    }
  }

  // Public API
  public hasConsent(category: CookieCategory): boolean {
    return this.preferences[category];
  }

  public getPreferences(): ConsentPreferences {
    return { ...this.preferences };
  }

  public revokeConsent(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.preferences = this.getDefaultPreferences();

    // Revoke Matomo
    if (window._paq) {
      window._paq.push(['forgetCookieConsentGiven']);
    }

    // Reload to clear all tracking
    window.location.reload();
  }
}

// Initialize on DOM ready
let cookieConsentInstance: CookieConsent | null = null;

document.addEventListener('DOMContentLoaded', () => {
  cookieConsentInstance = new CookieConsent();
});

// Export for external use
export function getCookieConsent(): CookieConsent | null {
  return cookieConsentInstance;
}
